import { defineStore } from 'pinia'

import { checkPermission, checkSomePermission } from '@/helpers/permissions-helper'

import { useAuthorizationStore } from '@/store/authorizationStore'

import { PERMISSION_NAMES } from '@/constants/workspace/permissionSettings'
import { ROUTES } from '@/constants/routes'

export const usePermissionsStore = defineStore('permissionsStore', {
    state: () => ({
        allPermissions: null,
    }),

    getters: {
        permissions(state) {
            return {
                reporting: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_reporting),
                    viewOnlyCurrentDay: checkPermission(state.allPermissions, PERMISSION_NAMES.view_only_current_day_data),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_reporting),
                    download: checkPermission(state.allPermissions, PERMISSION_NAMES.download_reports),
                    disputeEnabled: checkPermission(state.allPermissions, PERMISSION_NAMES.dispute_enabled),
                    listen: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.listen_to_recordings, PERMISSION_NAMES.see_record]),
                    blockNumbers: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_blocked_numbers, PERMISSION_NAMES.block_numbers]),
                    hangupCalls: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_reporting, PERMISSION_NAMES.hangup_calls]),
                },
                campaigns: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_campaigns),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_campaigns),
                    editTrackingNumbers: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.campaigns_edit_tracking_numbers, PERMISSION_NAMES.manage_traffic]),
                    editForwardCallsTo: checkPermission(state.allPermissions, PERMISSION_NAMES.campaigns_edit_forward_calls_to),
                    editAdvancedSettings: checkPermission(state.allPermissions, PERMISSION_NAMES.campaigns_edit_advanced_settings),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_campaigns),
                },
                rtbLog: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_rtb_bid_log),
                },
                callFlows: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_call_flows),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_call_flows),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_call_flows),
                },
                numbers: {
                    view: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.view_phone_numbers, PERMISSION_NAMES.manage_traffic]),
                    create: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_phone_numbers, PERMISSION_NAMES.create_numbers]),
                    edit: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_phone_numbers, PERMISSION_NAMES.manage_traffic]),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_phone_numbers),
                },
                numberPools: {
                    view: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.view_phone_number_pools, PERMISSION_NAMES.manage_traffic]),
                    edit: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_phone_number_pools, PERMISSION_NAMES.manage_traffic]),
                    delete: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.delete_phone_number_pools, PERMISSION_NAMES.manage_traffic]),
                },
                destinations: {
                    view: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.view_destinations, PERMISSION_NAMES.create_edit_destinations]),
                    edit: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_destinations, PERMISSION_NAMES.create_edit_destinations]),
                    editEndpoint: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_destinations, PERMISSION_NAMES.edit_endpoint, PERMISSION_NAMES.create_edit_destinations]),
                    pause: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_destinations, PERMISSION_NAMES.pause_destinations, PERMISSION_NAMES.create_edit_destinations]),
                    capacity: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_destinations, PERMISSION_NAMES.set_capacity, PERMISSION_NAMES.create_edit_destinations]),
                    concurrency: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_destinations, PERMISSION_NAMES.set_concurrency, PERMISSION_NAMES.create_edit_destinations]),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_destinations),
                },
                groups: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_destination_groups),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_destination_groups),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_destination_groups),
                },
                buyers: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_buyers),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_buyers),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_buyers),
                },
                vendors: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_vendors),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_vendors),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_vendors),
                },
                trafficSources: {
                    view: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.view_traffic_sources, PERMISSION_NAMES.manage_traffic]),
                    edit: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.edit_traffic_sources, PERMISSION_NAMES.manage_traffic]),
                    delete: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.delete_traffic_sources, PERMISSION_NAMES.manage_traffic]),
                },
                integrations: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_integrations),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_integrations),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_integrations),
                },
                voipShield: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_voip_black_lists),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_voip_black_lists),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_voip_black_lists),
                },
                tcpaShield: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_tcpa_shield),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_tcpa_shield),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_tcpa_shield),
                },
                leads: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_leads),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_leads),
                },
                disputes: {
                    view: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.view_disputes, PERMISSION_NAMES.dispute_enabled]),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_disputes),
                },
                blockedNumbers: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_blocked_numbers),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_blocked_numbers),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_blocked_numbers),
                },
                customParameters: {
                    view: checkSomePermission(state.allPermissions, [PERMISSION_NAMES.view_custom_parameters, PERMISSION_NAMES.manage_traffic]),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_custom_parameters),
                    delete: checkPermission(state.allPermissions, PERMISSION_NAMES.delete_custom_parameters),
                },
                transcription: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_transcription),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_transcription),
                },
                billing: {
                    view: checkPermission(state.allPermissions, PERMISSION_NAMES.view_billing),
                    edit: checkPermission(state.allPermissions, PERMISSION_NAMES.edit_billing),
                },
            }
        },

        settingsAccess() {
            const authorizationStore = useAuthorizationStore()

            return {
                workspaceOwner: authorizationStore.isWorkspaceOwner,
                hasAccount: authorizationStore.isOwner,
                whiteLabel: authorizationStore.isOwner,
                manageBilling: authorizationStore.isWorkspaceOwner || this.permissions.billing.edit,
            }
        },

        isAccountTrial() {
            const authorizationStore = useAuthorizationStore()

            return authorizationStore.isAccountTrial
        },

        isOwner() {
            const authorizationStore = useAuthorizationStore()

            return authorizationStore.isOwner
        },
    },

    actions: {
        setPermissions(payload) {
            this.allPermissions = payload.permissions
        },

        checkPermissionByName(permissionName) {
            return checkPermission(this.allPermissions, permissionName)
        },

        filterMenuByPermission(menuItems) {
            return menuItems
                .map((item) => {
                    if (item.section) {
                        return {
                            ...item,
                            isOpenSection: item.isOpenSection ?? true,
                            isFirstLoadSection: true,
                            items: this.filterMenuByPermission(item.items || []),
                        }
                    }

                    if (item.divider) {
                        return { ...item }
                    }

                    return {
                        ...item,
                        subMenu: item.subMenu ? [...item.subMenu] : undefined,
                        isOpenSub: false,
                        isFirstLoad: true,
                    }
                })
                .filter((item) => {
                    if (item.section) {
                        return item.items.length > 0
                    }

                    if (item.divider) {
                        return true
                    }

                    const hasPermission = item.permissions
                        ? this.checkAccountTrial(item.routeName) && item.permissions.some((permission) => checkPermission(this.allPermissions, permission))
                        : true

                    if (item.subMenu) {
                        item.subMenu = this.filterMenuByPermission(item.subMenu)
                    }

                    return hasPermission || (item.subMenu && item.subMenu.length > 0)
                })
        },

        checkAccountTrial(routeName) {
            if (!routeName || !this.isAccountTrial) {
                return true
            }

            switch (routeName) {
            case ROUTES.rtbLog:
            case ROUTES.pingPostLog:
                return false
            default:
                return true
            }
        },
    },
})
